import { type RootState } from "../../../store.ts"
import { selectActiveBranchId } from "../../Branches/redux/branches.slice.ts"
import { selectSurfacePreference } from "../../User/redux/ui-preferences.selectors.ts"
import { surfaceKey } from "../../User/redux/ui-preferences.types.ts"
import { buildLeadsRequestParams } from "./leads-request-params.ts"
import type { LeadsFilters } from "./leads.types.ts"

// The list's own remembered sort — set once a user picks a column and read
// back on every later visit, so leaving the page and coming back keeps
// whatever was last chosen instead of resetting. Last Activity is the
// fallback for anyone who has never picked one (matches the Projects list's
// own default, so the two behave identically).
export const LEADS_SORT_PREFERENCE_KEY = surfaceKey("leads.sort")
export const DEFAULT_LEADS_ORDERING = "-updated"

// "Date created" sorted by entry time under `created`; its successor, Lead
// Created, sorts by `created_on`. A sort saved before the rename reopens on it.
const RENAMED_ORDERINGS: Record<string, string> = {
  created: "created_on",
  "-created": "-created_on"
}
export const migrateLeadsOrdering = (value: string | null): string | null =>
  value ? (RENAMED_ORDERINGS[value] ?? value) : value

export type LeadsListQuery = LeadsFilters & { ordering?: string | null }

/** The query the Active list loads with: its remembered sort and the active
 *  branch beside the filters, always from page one. */
export const buildLeadsListQuery = (state: RootState): LeadsListQuery => {
  const ordering = migrateLeadsOrdering(
    selectSurfacePreference<string | null>(
      state,
      LEADS_SORT_PREFERENCE_KEY,
      DEFAULT_LEADS_ORDERING
    )
  )
  const activeBranchId = selectActiveBranchId(state)
  return {
    ordering,
    ...buildLeadsRequestParams(state.leads.filters),
    ...(activeBranchId ? { team: activeBranchId } : {}),
    page: 1
  }
}

/** What reloading the list on screen asks for: the query it last loaded
 *  with, or — before it has loaded — the one it would load with. */
export const selectLeadsReloadQuery = (state: RootState): LeadsListQuery =>
  state.leads.listQuery ?? buildLeadsListQuery(state)
